import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'
import type { ValueOf } from 'type-fest'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './permissions.translations'

// Expect the no access permission, every other permission name should match backend's permission name.
// As of today, their is only one permission type coming from the backend: EDIT
export const PERMISSION_TYPES = {
  NO_ACCESS: 'no_access',
  EDIT: 'edit',
} as const

export type ScopeName =
  | 'home'
  | 'insights'
  | 'customer_care'
  | 'listings'
  | 'orders'
  | 'opportunities'
  | 'invoices'
  | 'options'
  | 'reviews'
  | 'profile'
  | 'notification'

export type ScopePermissionType = ValueOf<typeof PERMISSION_TYPES>

export type Scope = {
  name: ScopeName
  translationKey: I18nDefinition
  defaultPermissionType: ScopePermissionType
  routeNames: string[]
}

// This constant represent all backend permissions we can have.
// The backend format to determine scope permissions is: SCOPE_NAME.PERMISSION_TYPE
// ex: orders.edit
// A scope generally consist on a route and all it's children / possible actions.
// Ex: the Orders scope correspond to all the Orders pages and related actions (cancel order, refund etc...)
// The routeNames property is an array of routes that are concerned by the permission.
// In few cases, we can have multiple routes concerned by the same page
// @ToDo: add the other scopes when associated routes will be migrated
export const SCOPES: Record<string, Scope> = {
  HOME: {
    name: 'home',
    translationKey: translations.home,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.HOME.INDEX],
  },
  INSIGHTS: {
    name: 'insights',
    translationKey: translations.insights,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.INSIGHTS.INDEX],
  },
  CUSTOMER_CARE: {
    name: 'customer_care',
    translationKey: translations.customerCare,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.CUSTOMER_CARE.INDEX],
  },
  LISTINGS: {
    name: 'listings',
    translationKey: translations.listings,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [
      ROUTE_NAMES.LISTINGS.INDEX,
      ROUTE_NAMES.LISTINGS.TABS.ACTIVE,
      ROUTE_NAMES.LISTINGS.TABS.ARCHIVED,
      ROUTE_NAMES.LISTINGS.TABS.IN_VALIDATION,
      ROUTE_NAMES.LISTINGS.TABS.MISSING_INFOS,
      ROUTE_NAMES.LISTINGS.PRODUCT.SEARCH,
      ROUTE_NAMES.LISTINGS.CREATE,
      ROUTE_NAMES.LISTINGSV2.INDEX,
      ROUTE_NAMES.LISTINGSV2.TABS.ACTIVE,
      ROUTE_NAMES.LISTINGSV2.TABS.ARCHIVED,
      ROUTE_NAMES.LISTINGSV2.TABS.IN_VALIDATION,
    ],
  },
  ORDERS: {
    name: 'orders',
    translationKey: translations.orders,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [
      ROUTE_NAMES.ORDERS.INDEX,
      // TODO
      // ROUTE_NAMES.SHIPPING.CREATE_LABEL,
    ],
  },
  OPPORTUNITIES: {
    name: 'opportunities',
    translationKey: translations.opportunities,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.OPPORTUNITIES.INDEX],
  },
  INVOICES: {
    name: 'invoices',
    translationKey: translations.invoices,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.INVOICES.INDEX],
  },
  OPTIONS: {
    name: 'options',
    translationKey: translations.options,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.OPTIONS.INDEX],
  },
  CUSTOMER_REVIEWS: {
    name: 'reviews',
    translationKey: translations.customerReviews,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.CUSTOMER_REVIEWS.INDEX],
  },
  PROFILE: {
    name: 'profile',
    translationKey: translations.profile,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [ROUTE_NAMES.PROFILE.INDEX],
  },
  FILE_LIBRARY: {
    name: 'notification',
    translationKey: translations.fileLibrary,
    defaultPermissionType: PERMISSION_TYPES.EDIT,
    routeNames: [],
  },
}
